import { currentKp, currentWind, weatherDomain, observationState } from '../utils/observations.js';
import { useEffect, useState } from 'react';

// ── helpers ──────────────────────────────────────────────────────────────────

function kpSeverity(kp) {
  if (kp == null || isNaN(kp)) return { label: '-', cls: '' };
  if (kp >= 7) return { label: `G${Math.min(5, Math.floor(kp - 4))}`, cls: 'alert' };
  if (kp >= 5) return { label: `G${Math.floor(kp - 4)}`, cls: 'warn'  };
  if (kp >= 4) return { label: 'Active', cls: 'warn' };
  if (kp >= 3) return { label: 'Unsettled', cls: '' };
  return            { label: 'Quiet', cls: 'good' };
}

function bzColor(bz) {
  if (bz == null || isNaN(bz)) return 'var(--txt-bright)';
  if (bz <= -10) return 'var(--warn)';
  if (bz <=  -5) return 'var(--amber)';
  return 'var(--green)';
}

function renderHeadline(text) {
  if (!text) return null;
  const m = text.match(/^(.*?)\b(\d+(?:\.\d+)?\s*(?:hours?|h\b|minutes?|min\b|days?))\b(.*)$/i);
  if (m) return <>{m[1]}<em>{m[2]}</em>{m[3]}</>;
  return text;
}

function defaultImpacts(kp) {
  if (kp == null) return [{ who: 'Observers', text: 'Current observations are unavailable. Storm impacts cannot be assessed.', severity: 'mod', tag: 'UNKNOWN' }];
  return [
    {who:'Geomagnetic activity',severity:kp>=5?'elev':'low',text:`Kp indicates ${kpSeverity(kp).label.toLowerCase()} conditions. Other solar observations may differ.`,tag:'Kp'},
    {who:'Aurora watchers',severity:'low',text:'Check local darkness and cloud conditions in Tonight alongside the solar forecast.',tag:'LOCAL'},
  ];
}

function chipTone(scale) {
  if (scale == null) return '';
  if (scale >= 4) return 'alert';
  if (scale >= 2) return 'warn';
  if (scale >= 1) return 'mod';
  return 'good';
}

function flareTone(letter) {
  if (letter === 'X') return 'alert';
  if (letter === 'M') return 'warn';
  if (letter === 'C') return 'mod';
  return 'good';
}

// Format "X minutes ago" from a Last-Modified style timestamp string.
// Returns null if the input can't be parsed.
function minutesAgo(lastModified) {
  if (!lastModified) return null;
  const t = Date.parse(lastModified);
  if (!Number.isFinite(t)) return null;
  const m = Math.round((Date.now() - t) / 60000);
  if (m < 1)   return 'just now';
  if (m < 60)  return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24)  return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

// ── component ────────────────────────────────────────────────────────────────

export default function SolarBriefing({
  health, weather, briefing, conditions, schumann, cmes = [], onSelectEvent, mobile = false,
}) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const kp     = currentKp(weather);
  const sev    = kpSeverity(kp);
  const bz     = currentWind(weather,'bz','mag');
  const bt     = currentWind(weather,'bt','mag');
  const wind   = currentWind(weather,'speed_kms_raw');
  const dens   = currentWind(weather,'density');
  const telemetryFresh = ['kp','wind','mag'].every(k=>weatherDomain(weather,k,now.getTime()).status === 'fresh');
  const summaryFresh = telemetryFresh && observationState(briefing?.status,briefing?.generated_at,now.getTime()) === 'fresh';
  const cmeSource = health?.sources?.find(s=>s.source === 'donki:cme' || s.source === 'donki');
  const cmesFresh = !health?.refresh_failed && cmeSource?.status === 'ok' && observationState('fresh',cmeSource?.last_success_at,now.getTime(),(cmeSource?.max_age_hours || 1.5)*3600000) === 'fresh';
  const conditionsFresh = observationState(conditions?.status,conditions?.observed_at,now.getTime()) === 'fresh';
  const kpHist = weather?.kp?.history || [];
  const inboundCount = cmes.filter(c => c.inbound).length;
  const inboundCme   = cmes.find(c => c.inbound && c.hours_to_arrival != null && c.hours_to_arrival > 0);

  const kpDelta = kp != null && kpHist.length > 12
    ? (kp - (kpHist[kpHist.length - 13]?.kp ?? kp)) * 100
    : null;
  const trend = kpDelta == null ? null
              : kpDelta >  20 ? { sym: '↗', cls: 'up',   label: 'rising'  }
              : kpDelta < -20 ? { sym: '↘', cls: 'down', label: 'falling' }
              : { sym: '·', cls: '', label: 'steady' };

  const gScale = conditionsFresh ? conditions.geomag?.scale : null;
  const rScale = conditionsFresh ? conditions.radio?.scale : null;
  const sScale = conditionsFresh ? conditions.radiation?.scale : null;
  const flare  = conditionsFresh ? conditions?.flare : null;

  // Schumann chip, no numeric scale, only a "live/stale" indicator based on
  // how long ago the upstream Cumiana spectrogram was modified. We treat
  // missing data as "good" (calm green) since the Earth-ionosphere cavity
  // itself is always there, only our visibility into it can drop out.
  const schumannAgeMin = (() => {
    if (!schumann?.upstreamLastModified) return null;
    const t = Date.parse(schumann.upstreamLastModified);
    if (!Number.isFinite(t)) return null;
    return Math.round((Date.now() - t) / 60000);
  })();
  const schumannCurrent = schumann?.cacheState !== 'stale' && observationState('fresh',schumann?.upstreamLastModified,now.getTime(),2*3600000) === 'fresh';
  const schumannTone = !schumannCurrent ? 'warn' : !schumannAgeMin            ? 'mod'   // unknown
                     : schumannAgeMin > 120       ? 'warn'  // stale > 2h
                     : 'good';                              // fresh
  const schumannLabel = !schumannCurrent ? 'Unavailable or stale' : schumannAgeMin == null
    ? (schumann?.cacheState === 'stale' ? 'source unreachable' : 'connecting…')
    : `updated ${minutesAgo(schumann.upstreamLastModified)}`;

  return (
    <div className={`og-solar-brief hg-brief-panel${mobile ? ' is-mobile' : ''}`}>

      <section>
        <div className="hg-rubric">
          <span>Situation · {now.toUTCString().slice(17, 22)} UT</span>
          <span className="hg-rubric-meta">{!briefing ? 'SUMMARY UNAVAILABLE' : summaryFresh ? (briefing.cached ? 'SUMMARY':'SUMMARY') : 'SUMMARY INCOMPLETE'}</span>
        </div>
        <h1 className="hg-headline">
          {summaryFresh && briefing?.headline ? renderHeadline(briefing.headline) : <em>{telemetryFresh ? 'Solar summary unavailable' : 'Solar observations incomplete'}</em>}
        </h1>
        <p className="hg-deck">
          {summaryFresh ? briefing.deck : 'Some observations or the summary are unavailable or stale. Missing values do not establish quiet conditions; check each source below.'}
          {briefing?.generated_at && <small style={{display:'block',marginTop:6}}>Summary generated {new Date(briefing.generated_at).toLocaleString()}</small>}
        </p>

      </section>

      <section>
        <div className="hg-rubric">
          <span>Now</span>
          <span className="hg-rubric-meta">{telemetryFresh ? 'SOLAR OBSERVATIONS' : 'OBSERVATIONS INCOMPLETE'}</span>
        </div>
        <div className="hg-metrics" style={{ marginTop: 8 }}>

          <div className="hg-metric">
            <div className="hg-metric-l">Kp index</div>
            <div className="hg-metric-row">
              <span className={`hg-metric-n ${sev.cls}`}>{(kp == null ? '-' : kp.toFixed(1))}</span>
              <span className="hg-metric-u">/ 9 · {sev.label}</span>
            </div>
            {trend && (
              <div className="hg-metric-sub">
                <span className={trend.cls}>{trend.sym}</span> {trend.label}
              </div>
            )}
          </div>

          <div className="hg-metric">
            <div className="hg-metric-l">IMF Bz</div>
            <div className="hg-metric-row">
              <span className="hg-metric-n" style={{ color: bzColor(bz) }}>
                {bz != null ? bz.toFixed(1) : '-'}
              </span>
              <span className="hg-metric-u">nT</span>
            </div>
            <div className="hg-metric-sub">
              {bz != null ? (bz < 0 ? 'southward' : 'northward') : ''}
              {bt != null ? ` · |B| ${bt.toFixed(1)} nT` : ''}
            </div>
          </div>

          <div className="hg-metric">
            <div className="hg-metric-l">Solar wind</div>
            <div className="hg-metric-row">
              <span className={`hg-metric-n ${wind > 600 ? 'warn' : ''}`}>
                {wind ? Math.round(wind) : '-'}
              </span>
              <span className="hg-metric-u">km/s</span>
            </div>
            <div className="hg-metric-sub">
              {dens ? `${dens.toFixed(1)} p/cm³` : 'density -'}
            </div>
          </div>

          <div className="hg-metric">
            <div className="hg-metric-l">CMEs · 72h</div>
            <div className="hg-metric-row">
              <span className={`hg-metric-n ${inboundCount > 0 ? 'warn' : 'good'}`}>
                {cmesFresh ? inboundCount : '-'}
              </span>
              <span className="hg-metric-u">inbound</span>
            </div>
            <div className="hg-metric-sub">
              {!cmesFresh ? 'CME observations unavailable' : inboundCme
                ? `next ETA ${inboundCme.hours_to_arrival.toFixed(1)}h`
                : 'no impacts pending'}
            </div>
          </div>

        </div>
      </section>

      {/* Storm-scale chips, G / R / S / Flare in 2×2, with Schumann spanning
          the full row below since it shows a status string, not a digit. */}
      <details className="og-solar-more">
        <summary>Storm scales & resonance</summary>
        <div className="hg-rubric">
          <span>Storm scales</span>
          <span className="hg-rubric-meta">NOAA · OBSERVED</span>
        </div>
        <div className="hg-chips" style={{ marginTop: 8 }}>

          <button
            className={`hg-chip ${chipTone(gScale)}`}
            onClick={() => onSelectEvent?.({
              id: 'scale-g', kind: 'scale-g',
              tone: chipTone(gScale) === 'alert' ? 'rose' : '',
              hours: 0,
              title: `Geomagnetic · G${gScale ?? '-'}`,
              meta:  conditionsFresh ? conditions.geomag?.label : 'Observations unavailable or stale',
              payload: { conditions, kp, kpHist },
            })}
          >
            <span className="hg-chip-icon">⚡</span>
            <span className="hg-chip-scale">G{gScale ?? '-'}</span>
            <span className="hg-chip-label">{conditionsFresh ? conditions.geomag?.label : 'Unavailable or stale'}</span>
          </button>

          <button
            className={`hg-chip ${chipTone(rScale)}`}
            onClick={() => onSelectEvent?.({
              id: 'scale-r', kind: 'scale-r',
              tone: chipTone(rScale) === 'alert' ? 'rose' : '',
              hours: 0,
              title: `Radio blackout · R${rScale ?? '-'}`,
              meta:  conditionsFresh ? conditions.radio?.label : 'Unavailable or stale',
              payload: { conditions },
            })}
          >
            <span className="hg-chip-icon">📡</span>
            <span className="hg-chip-scale">R{rScale ?? '-'}</span>
            <span className="hg-chip-label">{conditionsFresh ? conditions.radio?.label : 'Unavailable or stale'}</span>
          </button>

          <button
            className={`hg-chip ${chipTone(sScale)}`}
            onClick={() => onSelectEvent?.({
              id: 'scale-s', kind: 'scale-s',
              tone: chipTone(sScale) === 'alert' ? 'rose' : '',
              hours: 0,
              title: `Radiation storm · S${sScale ?? '-'}`,
              meta:  conditionsFresh ? conditions.radiation?.label : 'Unavailable or stale',
              payload: { conditions },
            })}
          >
            <span className="hg-chip-icon">☢</span>
            <span className="hg-chip-scale">S{sScale ?? '-'}</span>
            <span className="hg-chip-label">{conditionsFresh ? conditions.radiation?.label : 'Unavailable or stale'}</span>
          </button>

          <button
            className={`hg-chip ${flare ? flareTone(flare.class_letter) : 'good'}`}
            onClick={() => onSelectEvent?.({
              id: 'flare', kind: 'flare',
              tone: flare && flareTone(flare.class_letter) === 'alert' ? 'rose' : '',
              hours: 0,
              title: flare ? `Solar flare · ${flare.class_letter}${flare.mantissa != null ? flare.mantissa : ''}` : 'Solar flare',
              meta:  flare?.class_label || 'no data',
              payload: { flare },
            })}
          >
            <span className="hg-chip-icon">☀</span>
            <span className="hg-chip-scale">
              {flare ? `${flare.class_letter}${flare.mantissa != null ? flare.mantissa : ''}` : '-'}
            </span>
            <span className="hg-chip-label">
              {flare?.class_label || 'no data'}
            </span>
          </button>

          {/* Schumann chip spans both columns. No numeric scale, Schumann
              resonances are a continuous geophysical phenomenon, not a
              discrete NOAA-style level. The waveform glyph signals "open
              the chart to see what's happening." */}
          <button
            className={`hg-chip hg-chip-wide ${schumannTone}`}
            onClick={() => onSelectEvent?.({
              id: 'schumann', kind: 'schumann',
              tone: '',
              hours: 0,
              title: 'Schumann resonance · Cumiana',
              meta:  schumannLabel,
              payload: { schumann },
            })}
          >
            <span className="hg-chip-icon">
              <svg viewBox="0 0 32 12" width="22" height="10" fill="none"
                   stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
                <path d="M 1 6 Q 4 1, 7 6 T 13 6 T 19 6 T 25 6 T 31 6" />
              </svg>
            </span>
            {/* Badge must agree with the freshness label beside it, "LIVE ·
                updated 3d ago" reads as a contradiction. */}
            <span className="hg-chip-scale">
              {schumannTone === 'good' ? 'LIVE' : schumannTone === 'warn' ? 'STALE' : 'FEED'}
            </span>
            <span className="hg-chip-label">{schumannLabel} · 7.83 Hz cavity</span>
          </button>

        </div>
      </details>

      <section>
        <div className="hg-rubric">
          <span>Who's affected</span>
          <span className="hg-rubric-meta">PLAIN-ENGLISH IMPACT</span>
        </div>
        <ul className="hg-impact" style={{ marginTop: 4 }}>
          {(summaryFresh && briefing?.impacts?.length ? briefing.impacts : defaultImpacts(telemetryFresh ? kp : null))
            .slice(0, 4)
            .map((imp, i) => (
              <li key={i} className={`hg-imp ${imp.severity || 'mod'}`}>
                <span className={`hg-imp-dot ${imp.severity || 'mod'}`} />
                <span className="hg-imp-txt"><b>{imp.who}</b>, {imp.text}</span>
                <span className="hg-imp-tag">{imp.tag || ''}</span>
              </li>
            ))}
        </ul>
      </section>

    </div>
  );
}
